import {
  useGetDynamicBookingTextQuery,
  useGetNewBookingAccountDetailsQuery,
} from '../../../user_profile/userProfileSlice';
import { exotic_fishery } from '../../../common/Utils';
import React, { useState } from 'react';
import { Divider, Modal, Spin } from 'antd';
import AppDownloadLinks from '../../../app_download_links';
import appQR from '../../../home/assets/images/AppDownloadCropped.svg';
import greenCamo from '../../assets/images/green-camo.jpg';
import SvgGreenCheckCamo from '../../assets/images/green-camo-check';
import SvgReceipt from '../../assets/images/Receipt';
import { formatDateWithTime2 } from '../../../../utils/date';
import {
  isDawnOrDuskLabel,
  shouldUseDawnForBooking,
  shouldUseDuskForBooking,
  ticketSummaryArrivalLine,
  ticketSummaryDepartureLine,
} from '../../../../utils/bookingTimeDisplay';
import { costString } from '../../../../utils/cost';
import { useGetFisheryQuery } from '../../../fishery_profile/fisherySlice';
import gotEverythingBanner from '../../assets/images/have_everything_banner.svg';
import { useGetBrandsQuery } from '../../../brands/brandsSlice';
import WatercraftTicketStrip from '../booking_success/components/WatercraftTicketStrip';

export default function UserTicketSummary({
  fisheryPublicId,
  userId,
  bookingId,
  onWatercraftCtaClick,
}) {
  const [showAppLinkModal, setShowAppLinkModal] = useState(false);

  const { data: DynamicText } = useGetDynamicBookingTextQuery(fisheryPublicId, {
    skip: !fisheryPublicId,
  });

  const { data: fisheryInfo, isError: fisheryDataError } = useGetFisheryQuery(
    fisheryPublicId,
    {
      skip: !fisheryPublicId,
    }
  );

  const {
    data: brands,
    isLoading: loadingBrands,
    isError: brandsError,
  } = useGetBrandsQuery({
    tiers: ['tier1'],
  });

  const hasBrands =
    !fisheryDataError &&
    fisheryInfo?.allowed_marketing_tiers?.includes?.('tier1') &&
    !loadingBrands &&
    !brandsError &&
    brands?.length;

  const {
    data: accountDetails,
    isFetching: loadingDetails,
    error,
  } = useGetNewBookingAccountDetailsQuery({
    bookingId: bookingId,
    userId: userId,
  });

  const booking = accountDetails?.booking;

  if (loadingDetails) {
    return <Spin />;
  }

  if (!booking || error) {
    return <p>Something went wrong.</p>;
  }
  const reservationBooking = Boolean(booking?.package_booking_details);
  const lakeExclusive = booking?.exclusive;
  const lakeDetails = booking?.lakes?.[0];
  const lakeId = lakeDetails?.id;
  const accommodation = lakeDetails?.accommodation;

  const arrivalIso = booking?.arrival_ts ?? booking?.arrival;
  const departureIso = booking?.departure_ts ?? booking?.departure;
  const isArrivalFixed =
    isDawnOrDuskLabel(booking?.arrival_formatted) ||
    shouldUseDawnForBooking(arrivalIso, booking?.dawn_as_am_label) ||
    shouldUseDuskForBooking(arrivalIso, booking?.dusk_as_pm_label);
  const isDepartureFixed =
    isDawnOrDuskLabel(booking?.departure_formatted) ||
    shouldUseDawnForBooking(departureIso, booking?.dawn_as_am_label) ||
    shouldUseDuskForBooking(departureIso, booking?.dusk_as_pm_label);

  const userSummary = (() => {
    const users = booking?.accommodation_users;
    if (!users) return '';

    const parts = [];

    if (users.no_of_adults > 0) {
      parts.push(
        `${users.no_of_adults} x ${
          users.no_of_adults === 1 ? 'Adult' : 'Adults'
        }`
      );
    }

    if (users.no_of_children > 0) {
      parts.push(
        `${users.no_of_children} x ${
          users.no_of_children === 1 ? 'Child' : 'Children'
        }`
      );
    }
    if (users.no_of_teens > 0) {
      parts.push(
        `${users.no_of_teens} x ${users.no_of_teens === 1 ? 'Teen' : 'Teens'}`
      );
    }

    return parts.join(' | ');
  })();
  const is_exotic_fishery = fisheryPublicId === exotic_fishery;
  const is_exotic_arapaima_lake =
    lakeId === 'a4688274-3f67-40a5-be8d-a8dc51c053b3';

  const appModal = (
    <Modal
      width='min(90%, 40rem)'
      centered
      open={showAppLinkModal}
      onCancel={() => setShowAppLinkModal(false)}
      title={null}
      footer={null}
      className={'appModal'}
    >
      <AppDownloadLinks />
      <div className='desktop-qrs pd-40 pd-left-60 pd-right-60 txt-center'>
        {['Apple', 'Android'].map((device) => (
          <div key={`QR-${device}`}>
            <div className='gSemiBold fs-18 lh-1.25 grey-900 text'>
              Scan with the camera on your {device} device:
            </div>
            <div
              className={
                'qr-wrapper sb-blue-bg-500 white uppercase gSemiBold fs-20 pd-8'
              }
            >
              <img
                src={appQR}
                width={150}
                alt={'download QR codes for the swimbooker app'}
              />
              Scan Me
            </div>
          </div>
        ))}
      </div>
    </Modal>
  );
  // const numTickets = booking.angler_details.length;
  // const numSwims =
  //   (userSelectedSwims?.length || 0) + (userSelectedDoubleSwim?.length || 0);
  // const selectedSwimNames = (swimAvailableData?.swims || [])
  //   .filter((s) => userSelectedSwims?.includes(s.id))
  //   .map((s) => s.name);

  const summaryFieldClass =
    'fs-15 gMedium mg-bottom-2 display-flex-row justify-between';
  const labelClass = 'gBold ';

  return (
    <div
      className={'br20 white txt-center user-booking-ticket'}
      style={{
        backgroundImage: `url(${greenCamo})`,
        backgroundColor: '#60bf97',
        backgroundSize: 'cover',
      }}
      id={'ticket'}
    >
      {appModal}
      <div className='pd-20 ticket-main'>
        <div className='ticket-top-icon'>
          <SvgGreenCheckCamo className='fs-60' />
        </div>
        <div className='seagreen-bg-100 seagreen-800 gBold fs-13 w-fit h-fit pd-8 pd-left-12 pd-right-12 brFull txt-center mg-top-15 mg-bottom-12 mg-auto-lr lh-1 display-flex-row gap-05r'>
          <SvgReceipt /> {reservationBooking ? 'Reservation' : 'Booking'} ID: #
          {reservationBooking ? booking?.public_id : bookingId}
        </div>
        <h1 className='white gBold fs-22'>Thank you for your booking!</h1>
        <p className='gMedium fs-14 seagreen-100 lh-1.25'>
          A confirmation email containing your ticket(s) has been sent to your
          email address.
        </p>
        <Divider
          className='ticket-divider seagreen-border-100'
          style={{ borderTopStyle: 'dashed', borderTopWidth: 2 }}
        />
        <p className={summaryFieldClass}>
          <span className={labelClass}>Venue</span> {fisheryInfo?.venue_name}
        </p>
        {fisheryPublicId !== exotic_fishery && (
          <p className={summaryFieldClass}>
            {DynamicText?.is_active &&
            DynamicText?.properties?.booking_step_2?.booking_type ? (
              ''
            ) : (
              <span className={labelClass}>
                {accommodation || reservationBooking ? 'Booked' : 'Lake'}:
              </span>
            )}
            {reservationBooking
              ? booking?.accommodation_details
                  .filter((item) => item.quantity > 0)
                  .map((item) => {
                    const pluralizedName = item.ticket_type_name;
                    return `${item.quantity} x ${pluralizedName}`;
                  })
                  .join(' | ')
              : lakeDetails?.name}
          </p>
        )}
        {!accommodation && !reservationBooking && (
          <p className={summaryFieldClass}>
            <span className={labelClass}>
              {DynamicText?.is_active &&
              DynamicText?.properties?.booking_summary?.lake_text
                ? DynamicText?.properties?.booking_summary?.lake_text
                : 'Booked'}
              :
            </span>{' '}
            {DynamicText?.is_active &&
            DynamicText?.properties?.booking_summary?.lake_text
              ? booking?.custom_slot?.name ?? booking?.booking_description
              : booking?.booking_description}
          </p>
        )}
        {(!lakeExclusive || lakeDetails?.lake_exclusive_anglers_allowed) &&
          !accommodation && (
            <p className={summaryFieldClass}>
              {DynamicText?.is_active ? (
                DynamicText?.properties?.booking_step_1?.lake_exclusive_label
              ) : (
                <>
                  <span className={labelClass}>
                    {lakeExclusive ? 'Max ' : ''}
                    {(is_exotic_fishery && !is_exotic_arapaima_lake) ||
                    reservationBooking
                      ? 'No. of Guests:'
                      : 'No. of Anglers:'}
                  </span>
                  {reservationBooking
                    ? userSummary
                    : lakeExclusive
                      ? lakeDetails.lake_exclusive_anglers_allowed
                      : booking?.total_anglers ?? 0}
                </>
              )}
            </p>
          )}
        {!lakeExclusive && !accommodation && !is_exotic_arapaima_lake && (
          <p className={summaryFieldClass}>
            <span className={labelClass}>
              {fisheryPublicId !== exotic_fishery && !reservationBooking
                ? 'No. of Guests:'
                : 'No. of Anglers:'}
            </span>{' '}
            {reservationBooking
              ? booking?.fishing_details?.number_of_anglers
              : booking?.guest_anglers ?? 0}
          </p>
        )}
        <p className={summaryFieldClass}>
          <span className={labelClass}>Arrival:</span>{' '}
          {isArrivalFixed
            ? ticketSummaryArrivalLine(booking)
            : reservationBooking
              ? booking.arrival_ts
                ? formatDateWithTime2(booking.arrival_ts)
                : ''
              : formatDateWithTime2(booking.arrival)}
        </p>
        <p className={summaryFieldClass}>
          <span className={labelClass}>Departure:</span>{' '}
          {isDepartureFixed
            ? ticketSummaryDepartureLine(booking)
            : reservationBooking
              ? booking.departure_ts
                ? formatDateWithTime2(booking.departure_ts)
                : ''
              : formatDateWithTime2(booking.departure)}
        </p>
        {reservationBooking ? (
          <>
            <p className={summaryFieldClass}>
              <span className={labelClass}>Amount Paid:</span>{' '}
              {fisheryInfo?.currency}
              {booking?.deposit}
            </p>
            <p className={summaryFieldClass}>
              <span className={labelClass}>Balance Owed:</span>{' '}
              {fisheryInfo?.currency}
              {booking?.balance}
            </p>
            <p className={summaryFieldClass}>
              <span className={labelClass}>Total Booking Value:</span>{' '}
              {fisheryInfo?.currency}
              {booking?.total_price}
            </p>
          </>
        ) : (
          <p className={summaryFieldClass}>
            <span className={labelClass}>Paid:</span>{' '}
            {costString(booking?.payable_amount)}
          </p>
        )}
        {onWatercraftCtaClick && (
          <WatercraftTicketStrip onTellMeMore={onWatercraftCtaClick} />
        )}
      </div>
      <div
        onClick={() => setShowAppLinkModal(true)}
        className='ticket-footer pd-10 gBold fs-16 seagreen-bg-100 seagreen-border-100 border-1 seagreen-800 txt-underline cp'
      >
        Where can I find the swimbooker&trade; app? 📲
      </div>
    </div>
  );
}
